import packageJson from '../../../package.json';
import { useEffect, useRef, useState } from 'react';
import {
  ArrowRightOnRectangleIcon,
  BellIcon,
  ChevronDownIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon
} from '@heroicons/react/24/outline';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from 'auth/AuthContext';
import DashboardNav from 'components/navs/DashboardNav';
import { useTheme } from 'theme/ThemeContext';
import { useTranslation } from 'react-i18next';

const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { themePreference, setThemePreference, resolvedTheme } = useTheme();
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Alex Rivera';
  const userRole = user?.role || user?.position || 'Operations Manager';
  const userInitials = userName
    .split(' ')
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const isManagementRoute = location.pathname.startsWith('/management');
  const themeOptions = [
    { value: 'light', icon: SunIcon, label: t('settings.themeLight') },
    { value: 'dark', icon: MoonIcon, label: t('settings.themeDark') },
    { value: 'system', icon: ComputerDesktopIcon, label: t('settings.themeSystem') }
  ];
  const languageOptions = [
    { value: 'es-MX', label: 'Español', shortLabel: 'ES' },
    { value: 'en-US', label: 'English', shortLabel: 'EN' }
  ];
  const activeLanguage = i18n.resolvedLanguage || i18n.language || 'es-MX';

  const handleLanguageChange = language => {
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
  };

  const handleLogout = () => {
    setIsAccountMenuOpen(false);
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const handlePointerDown = event => {
      if (!accountMenuRef.current?.contains(event.target)) {
        setIsAccountMenuOpen(false);
      }
    };

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div
      className={`min-h-screen workspace-shell theme-${resolvedTheme} ${isManagementRoute ? 'workspace-shell--management' : 'workspace-shell--dashboard'}`}
    >
      <DashboardNav />

      <main className="min-h-screen md:ml-64">
        <header className="workspace-header sticky top-0 z-40 px-4 py-3 backdrop-blur-xl sm:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-6 lg:flex-1">
              <div className="workspace-search relative w-full max-w-md">
                <span className="workspace-search-icon pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg">
                  ⌕
                </span>
                <input
                  type="text"
                  readOnly
                  value=""
                  placeholder="Search workspace..."
                  className="workspace-search-input w-full rounded-md py-2 pl-10 pr-4 text-sm outline-none"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <Link
                to="/help"
                className="workspace-header-link text-sm font-semibold transition-colors"
              >
                Support
              </Link>

              <div className="flex items-center gap-3">
                <button
                  className="workspace-icon-button relative rounded-full p-2 transition-colors"
                  aria-label="Notifications"
                  title="Notifications"
                >
                  <BellIcon className="h-5 w-5" />
                  <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(75,226,119,0.85)]" />
                </button>
              </div>

              <div className="workspace-user-panel relative pl-3 sm:pl-5" ref={accountMenuRef}>
                <button
                  type="button"
                  className="workspace-account-trigger flex items-center gap-3 transition-colors"
                  aria-label={t('nav.accountMenu')}
                  aria-haspopup="menu"
                  aria-expanded={isAccountMenuOpen}
                  onClick={() => setIsAccountMenuOpen(open => !open)}
                >
                  <span className="workspace-user-meta hidden text-right sm:block">
                    <span className="block text-xs font-bold">{userName}</span>
                    <span className="font-command-label block text-[10px] uppercase tracking-[0.12em]">
                      {userRole}
                    </span>
                  </span>
                  <span className="workspace-avatar flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold">
                    {userInitials}
                  </span>
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform ${isAccountMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isAccountMenuOpen && (
                  <div className="workspace-account-menu absolute right-0 top-[calc(100%+0.6rem)] z-50 w-[min(18rem,calc(100vw-2rem))] rounded-md p-3 shadow-xl">
                    <div className="workspace-account-menu-profile flex items-center gap-3 pb-3">
                      <span className="workspace-avatar flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold">
                        {userInitials}
                      </span>
                      <span>
                        <span className="block text-sm font-bold">{userName}</span>
                        <span className="font-command-label block text-[10px] uppercase tracking-[0.12em]">
                          {userRole}
                        </span>
                      </span>
                    </div>

                    <div className="workspace-account-menu-section py-3">
                      <p className="workspace-account-menu-section-label">
                        {t('settings.language')}
                      </p>
                      <div
                        className="workspace-language-toggle mt-2 flex w-fit items-center rounded-md p-1"
                        aria-label="Language selector"
                      >
                        {languageOptions.map(({ value, label, shortLabel }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => handleLanguageChange(value)}
                            className={`workspace-language-option ${activeLanguage === value ? 'workspace-language-option--active' : ''}`}
                            aria-label={`Switch language to ${label}`}
                            title={label}
                          >
                            {shortLabel}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="workspace-account-menu-section py-3">
                      <p className="workspace-account-menu-section-label">
                        {t('settings.appearance')}
                      </p>
                      <div className="workspace-theme-toggle mt-2 flex w-fit items-center rounded-md p-1">
                        {themeOptions.map(({ value, icon: Icon, label }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setThemePreference(value)}
                            className={`workspace-theme-option ${themePreference === value ? 'workspace-theme-option--active' : ''}`}
                            aria-label={`Switch theme to ${label}`}
                            title={label}
                          >
                            <Icon className="h-4 w-4" />
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleLogout}
                      className="workspace-logout-button workspace-logout-button--menu w-full text-sm font-semibold transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4" />
                      <span>{t('nav.logout')}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <DashboardNav compact />

        <div className="flex min-h-[calc(100vh-65px)] flex-col">
          <div
            className={`min-w-0 flex-1 ${isManagementRoute ? 'workspace-content workspace-content--management' : 'workspace-content workspace-content--dashboard'}`}
          >
            <Outlet />
          </div>

          <footer className="workspace-footer mt-auto px-4 py-3 sm:px-6">
            <div className="workspace-footer-copy flex flex-col gap-4 text-[10px] uppercase tracking-[0.2em] lg:flex-row lg:items-center lg:justify-between">
              <p>
                © 2024 {t('dashboard.sidebarTitle')} / {t('dashboard.sidebarSubtitle')} v
                {packageJson.version}
              </p>
              <div className="flex flex-wrap items-center gap-6 lg:gap-8">
                <Link to="/help" className="workspace-footer-link transition-colors">
                  Privacy Protocol
                </Link>
                <Link to="/help" className="workspace-footer-link transition-colors">
                  API Documentation
                </Link>
                <Link to="/help" className="workspace-footer-link transition-colors">
                  System Status: Online
                </Link>
              </div>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
